import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, ArrowLeft, Mail, Eye, MousePointerClick, Users, TrendingUp, Award,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const httpAuth = () => ({ Authorization: `Bearer ${localStorage.getItem("wt_token")}` });

export default function DigestAnalytics() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [days, setDays]     = useState(60);
  const [data, setData]     = useState(null);
  const [forbidden, setForbidden] = useState(false);

  useEffect(() => {
    document.title = "Digest Analytics | WebsiteTech.pro";
    if (user === undefined) return;
    if (!user) { setLoading(false); return; }
    (async () => {
      try {
        const { data } = await axios.get(
          `${API}/ai-visibility/digest/analytics?days=${days}`,
          { headers: httpAuth() });
        setData(data);
      } catch (e) {
        if (e?.response?.status === 403) setForbidden(true);
        else toast.error("Could not load analytics");
      } finally { setLoading(false); }
    })();
  }, [user, days]);

  if (loading) return (
    <div className="min-h-screen grid place-items-center bg-slate-50">
      <Loader2 className="w-6 h-6 animate-spin text-slate-500" />
    </div>
  );
  if (!user || forbidden) return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <main className="max-w-3xl mx-auto px-4 py-16 text-center">
        <h1 className="text-2xl font-bold text-slate-900">Admin only</h1>
        <p className="text-slate-600 mt-1">This dashboard is available to admins.</p>
        {!user && (
          <Link to="/login?redirect=/admin/digest-analytics"
                className="mt-4 inline-flex items-center gap-1 rounded-lg bg-slate-900 text-white px-4 py-2 text-sm font-bold">
            Sign in
          </Link>
        )}
      </main>
    </div>
  );
  if (!data) return null;

  const t = data.totals;
  return (
    <div className="min-h-screen bg-slate-50" data-testid="digest-analytics-page">
      <Navbar />
      <main className="max-w-6xl mx-auto px-4 py-8 grid gap-6">
        <div>
          <Link to="/ai-visibility-monitor"
                data-testid="analytics-back"
                className="text-sm text-slate-500 hover:text-slate-900 mb-3 inline-flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" /> Back to AI Visibility
          </Link>
          <div className="text-[11px] uppercase tracking-widest text-fuchsia-600 font-bold">
            Admin · Weekly digest performance
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mt-1"
              data-testid="analytics-title">Digest Analytics</h1>
          <p className="text-slate-600 mt-1 max-w-2xl">
            Is the Monday recap actually landing? Track opens, clicks, and top-engaged subscribers over the last {days} days.
          </p>
          <div className="mt-3 inline-flex bg-white rounded-lg ring-1 ring-slate-200 p-0.5">
            {[7, 30, 60, 90, 180].map(d => (
              <button key={d} onClick={() => setDays(d)}
                      data-testid={`analytics-range-${d}`}
                      className={`px-3 py-1 text-xs font-bold rounded-md ${
                        days === d ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-900"}`}>
                {d}d
              </button>
            ))}
          </div>
        </div>

        <section className="grid sm:grid-cols-2 md:grid-cols-5 gap-3"
                 data-testid="analytics-totals">
          <Kpi icon={Users}                label="Subscribers" value={t.subscribers} tone="slate" />
          <Kpi icon={Mail}                 label="Sent"        value={t.sent}        tone="sky" />
          <Kpi icon={Eye}                  label="Opened"      value={t.opened}      suffix={`${t.open_rate}%`}   tone="emerald" />
          <Kpi icon={MousePointerClick}    label="Clicked"     value={t.clicked}     suffix={`${t.click_rate}%`}  tone="orange" />
          <Kpi icon={TrendingUp}           label="Click-thru rate" value={`${t.click_rate}%`} tone="fuchsia" />
        </section>

        <section className="rounded-xl bg-white ring-1 ring-slate-200 p-5"
                 data-testid="analytics-ab">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h2 className="font-bold text-slate-900">A/B subject-line test</h2>
            <div className="flex flex-wrap items-center gap-2">
              {data.ab?.locked_variant && (
                <span className="inline-flex items-center gap-1 rounded-full bg-fuchsia-50 ring-1 ring-fuchsia-200 text-fuchsia-700 px-3 py-1 text-xs font-bold"
                       data-testid="analytics-ab-lock">
                  <Award className="w-3.5 h-3.5" />
                  Locked: variant {data.ab.locked_variant}
                  {data.ab.locked_reason === "auto_2_week_streak" && " (auto)"}
                  {data.ab.locked_reason === "manual" && " (manual)"}
                </span>
              )}
              {data.ab?.locked_variant && (
                <button onClick={async () => {
                          try {
                            await axios.post(`${API}/ai-visibility/digest/ab-lock`,
                              { variant: null }, { headers: httpAuth() });
                            toast.success("Unlocked — A/B split resumes next send");
                            setDays(d => d); // trigger refetch
                          } catch { toast.error("Unlock failed"); }
                        }}
                        data-testid="analytics-ab-unlock"
                        className="text-xs text-rose-600 font-bold hover:underline">
                  Unlock
                </button>
              )}
              {!data.ab?.locked_variant && data.ab?.winner && (
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 ring-1 ring-emerald-200 text-emerald-700 px-3 py-1 text-xs font-bold"
                       data-testid="analytics-ab-winner">
                  <Award className="w-3.5 h-3.5" />
                  Winner: variant {data.ab.winner}
                </span>
              )}
              {!data.ab?.locked_variant && !data.ab?.winner
                  && data.ab?.min_sample_hit === false
                  && (data.ab?.variants || []).length > 0 && (
                <span className="text-xs text-slate-500">
                  Need 3+ sends per variant to declare a winner.
                </span>
              )}
            </div>
          </div>
          {data.ab?.streak_variant && !data.ab?.locked_variant && (
            <div className="mt-2 text-xs text-slate-500"
                 data-testid="analytics-ab-streak">
              Variant {data.ab.streak_variant} on a {data.ab.streak_len}-week winning streak.
              {data.ab.streak_len >= 2
                ? " Auto-lock will engage on next analytics refresh."
                : ` One more week ${data.ab.streak_variant === "A" ? "for A" : "for B"} to auto-lock.`}
            </div>
          )}
          {(data.ab?.variants || []).length === 0 ? (
            <div className="text-sm text-slate-500 py-4">
              No A/B data yet — subject lines start splitting on the next send.
            </div>
          ) : (
            <div className="mt-3 grid sm:grid-cols-2 gap-3">
              {data.ab.variants.map(v => (
                <div key={v.variant}
                     className={`rounded-xl ring-1 p-4 ${
                       data.ab.winner === v.variant
                         ? "bg-emerald-50 ring-emerald-300"
                         : "bg-slate-50 ring-slate-200"}`}
                     data-testid={`analytics-ab-${v.variant}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] uppercase tracking-widest font-bold text-slate-500">
                      Variant {v.variant} · {v.label}
                    </span>
                    {data.ab.winner === v.variant && (
                      <Award className="w-4 h-4 text-emerald-600" />
                    )}
                  </div>
                  <div className="mt-1 flex items-baseline gap-4">
                    <div>
                      <div className="text-2xl font-extrabold text-slate-900">{v.click_rate}%</div>
                      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">click rate</div>
                    </div>
                    <div>
                      <div className="text-lg font-bold text-slate-700">{v.open_rate}%</div>
                      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">open</div>
                    </div>
                    <div>
                      <div className="text-lg font-bold text-slate-700">{v.sent}</div>
                      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">sent</div>
                    </div>
                  </div>
                  {v.sample_subjects?.length > 0 && (
                    <div className="mt-3 border-t border-slate-200 pt-2">
                      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">Sample subject</div>
                      <div className="text-xs text-slate-700 italic">
                        "{v.sample_subjects[0]}"
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="rounded-xl bg-white ring-1 ring-slate-200 p-5"
                 data-testid="analytics-weeks">
          <h2 className="font-bold text-slate-900">Week-by-week</h2>
          {data.weeks.length === 0 && (
            <div className="text-sm text-slate-500 py-4">No digests sent in this window yet.</div>
          )}
          {data.weeks.length > 0 && (
            <table className="w-full text-sm mt-3">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest font-bold text-slate-500 text-left">
                  <th className="py-2">Week</th>
                  <th className="py-2">Sent</th>
                  <th className="py-2">Opened</th>
                  <th className="py-2">Clicked</th>
                  <th className="py-2">Open %</th>
                  <th className="py-2">Click %</th>
                </tr>
              </thead>
              <tbody>
                {data.weeks.map(w => (
                  <tr key={w.week} className="border-t border-slate-100">
                    <td className="py-2 font-mono text-xs">{w.week}</td>
                    <td className="py-2 font-bold">{w.sent}</td>
                    <td className="py-2">{w.opened}</td>
                    <td className="py-2">{w.clicked}</td>
                    <td className="py-2">
                      <RateBar pct={w.open_rate} tone="emerald" />
                    </td>
                    <td className="py-2">
                      <RateBar pct={w.click_rate} tone="orange" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        <section className="rounded-xl bg-white ring-1 ring-slate-200 p-5"
                 data-testid="analytics-top">
          <h2 className="font-bold text-slate-900">Top engaged subscribers</h2>
          {data.top_engaged.length === 0 && (
            <div className="text-sm text-slate-500 py-4">No engagement yet.</div>
          )}
          {data.top_engaged.length > 0 && (
            <table className="w-full text-sm mt-3">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest font-bold text-slate-500 text-left">
                  <th className="py-2">Subscriber</th>
                  <th className="py-2">Sent</th>
                  <th className="py-2">Opened</th>
                  <th className="py-2">Clicked</th>
                  <th className="py-2">Open %</th>
                </tr>
              </thead>
              <tbody>
                {data.top_engaged.map(r => (
                  <tr key={r.email} className="border-t border-slate-100">
                    <td className="py-2 text-slate-800">{r.email}</td>
                    <td className="py-2">{r.sent}</td>
                    <td className="py-2">{r.opened}</td>
                    <td className="py-2 font-bold">{r.clicked}</td>
                    <td className="py-2">{r.open_rate}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      </main>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, suffix, tone }) {
  const tint = {
    slate:    "bg-slate-50 text-slate-700 ring-slate-200",
    sky:      "bg-sky-50 text-sky-700 ring-sky-200",
    emerald:  "bg-emerald-50 text-emerald-700 ring-emerald-200",
    orange:   "bg-orange-50 text-orange-700 ring-orange-200",
    fuchsia:  "bg-fuchsia-50 text-fuchsia-700 ring-fuchsia-200",
  }[tone] || "bg-slate-50 text-slate-700 ring-slate-200";
  return (
    <div className={`rounded-xl ring-1 p-4 ${tint}`}>
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4" />
        <span className="text-[10px] uppercase tracking-widest font-bold">{label}</span>
      </div>
      <div className="mt-1 text-3xl font-extrabold">{value}</div>
      {suffix && <div className="text-xs opacity-70 mt-0.5">{suffix}</div>}
    </div>
  );
}

function RateBar({ pct, tone }) {
  const bg = tone === "orange" ? "bg-orange-500" : "bg-emerald-500";
  return (
    <div className="flex items-center gap-2">
      <div className="w-32 h-2 bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full ${bg}`} style={{ width: `${Math.min(pct, 100)}%` }} />
      </div>
      <span className="text-xs font-bold text-slate-700">{pct}%</span>
    </div>
  );
}
